/* eslint-disable @typescript-eslint/no-unsafe-call */
import {
    ActionIcon,
    Button,
    FocusTrap,
    InputWrapper,
    Loader,
    Popover,
    Select,
    Switch,
    TextInput,
    Textarea,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useToggle } from '@mantine/hooks';
import { Pencil, Trash2 } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { CollectionConfigurator } from '@/components/AppointmentScheduler/CollectionConfigurator.tsx';
import { CustomAppointmentTypeSelect } from '@/components/AppointmentScheduler/CustomAppointmentSelect.tsx';
import { CustomCalendarSelect } from '@/components/AppointmentScheduler/CustomCalendarSelect.tsx';
import TimeslotCalendarAccordion from '@/components/AppointmentScheduler/TimeslotCalendarAccordion.tsx';
import { AppointmentModeSelector } from '@/components/AppointmentScheduler/appointment-mode-selector.tsx';
import { PatientTypeSwitch } from '@/components/AppointmentScheduler/patient-type-switch.tsx';
import DateTimePickerInput from '@/components/DateTimePickerInput.tsx';
import Icon from '@/components/Icon.tsx';
import ImportanceSelector from '@/components/ImportanceSelector.tsx';
import OverlaySection from '@/components/OverlaySection.tsx';
import { ChainBookingConfigurator } from '@/components/chain-booking/chain-booking-configurator.tsx';
import Form from '@/components/form/Form.tsx';
import FormSection from '@/components/form/FormSection.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { PICKER_INTERACTION_KEY } from '@/constants/availability-telemetry.ts';

import { useAppointmentChainConfigs } from '@/hooks/admin/use-appointment-chain-configs.ts';
import useConfig from '@/hooks/useConfig.ts';

import {
    abortAvailabilityInteraction,
    settleAvailabilityInteraction,
    startAvailabilityInteraction,
} from '@/lib/availability-telemetry.ts';

import { Insurance } from '@/models/appointment/Appointment.ts';
import {
    AppFormInitialState,
    AppointmentBookingFormValues,
} from '@/models/appointment/AppointmentBookingFormValues.ts';
import { ChainBookingStep } from '@/models/appointment/AppointmentChainConfig.ts';
import { AppointmentTypeInsurances } from '@/models/appointment/AppointmentTypeInsurances.ts';
import { AppointmentFormOptions, AppointmentFormStep as Step } from '@/models/appointment/BookingFormOptions.ts';
import { EnrichedPractitionerData } from '@/models/appointment/EnrichedPractitionerData.ts';
import { AppointmentType } from '@/models/doc-cirrus/DCScheduleTypeMapping.ts';

import { book_appointment_form_schema } from '@/schemas/BookAppointmentForm.schema.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';
import { useConfigStore } from '@/stores/configStore.ts';

import { getSingleCalendarId } from '@/utils/comboboxUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { checkInsuranceIsOffered, insuranceToInsuranceType } from '@/utils/insurance-utils.ts';
import {
    APPOINTMENT_MODE,
    AppointmentMode,
    buildCalendarOptionsByMode,
    filterChainConfigs,
    getAppointmentModeSelectorData,
    getDefaultDurationFromAppointmentTypeId,
    getFormOptions,
} from '@/utils/practitioner/bookingFormOptionsUtils.ts';
import { filterEnrichedDataByPracticeId } from '@/utils/practitioner/cachedDataUtils.ts';
import { cn } from '@/utils/styleUtils.ts';

import DurationInput from './DurationInput.tsx';

interface Props {
    setIsFormValid: (value: boolean) => void;
    setFormData: (form_data: AppointmentBookingFormValues) => void;
    initialState: AppFormInitialState;
    onDelete: () => void;
    disableCancel: boolean;
    allowTransition: boolean;
    enableSteps: boolean;
    formOptionData?: EnrichedPractitionerData;
    isFormOptionDataLoading: boolean;
    options: {
        enable_priority?: boolean;
        enable_location?: boolean;
    };
}

export const AppointmentForm = ({
    setIsFormValid,
    setFormData,
    initialState,
    onDelete,
    disableCancel,
    allowTransition,
    enableSteps,
    formOptionData,
    isFormOptionDataLoading,
    options = { enable_priority: false, enable_location: false },
}: Props) => {
    const { values: initialValues, initialStep } = initialState;
    const [selectedPracticeId, setSelectedPracticeId] = useState<string | undefined>(
        initialValues?.practice_doc_cirrus_id
    );
    const [selectedCalendarId, setSelectedCalendarId] = useState<string | undefined>(initialValues?.calendar_pvs_id);
    const [selectedAppointmentTypeId, setSelectedAppointmentTypeId] = useState<string | undefined>(
        initialValues?.apt_type_id_or_schedule_type_pvs_id
    );
    const [defaultStartDate, setDefaultStartDate] = useState<string | undefined>();

    const {
        isSeriesMode,
        editingCollectionId,
        isSingleCollectionEdit,
        editingAppointment,
        isChainMode,
        chainBookingStep,
        appointmentsFormDraftCount,
        isReturningPatient,
        isBookingLoading,
    } = useBookingStore(
        useShallow((state) => ({
            isSeriesMode: state.isSeriesMode,
            editingCollectionId: state.editingCollectionId,
            isSingleCollectionEdit: state.isSingleCollectionEdit,
            editingAppointment: state.editing_appointment,
            isChainMode: state.is_chain_mode,
            chainBookingStep: state.chain_booking_step,
            appointmentsFormDraftCount: state.appointments_form_draft.length,
            isReturningPatient: state.is_returning_patient,
            isBookingLoading: state.is_booking_loading,
        }))
    );
    const setIsChainMode = useBookingStore((state) => state.setIsChainMode);
    const setIsSeriesModeStore = useBookingStore((state) => state.setIsSeriesMode);
    const resetChainSelection = useBookingStore((state) => state.resetChainSelection);
    const { customer_id: customerId } = useConfig();
    const {
        chainConfigs,
        isLoading: isChainsLoading,
        error: chainsError,
        mutate: refetchChains,
    } = useAppointmentChainConfigs({ customerId: customerId, includeEligiblePractitioners: true });
    // True whenever appointment type selection should be locked due to any series-related edit mode being active
    const isSeriesAppointmentTypeLocked = !!editingCollectionId || isSingleCollectionEdit;

    const getSelectedMode = (): AppointmentMode => {
        if (isChainMode) return APPOINTMENT_MODE.CHAINS;
        if (isSeriesMode || isSingleCollectionEdit) return APPOINTMENT_MODE.SERIES;
        return APPOINTMENT_MODE.SINGLE;
    };
    const selectedMode = getSelectedMode();
    const setSelectedMode = (val: AppointmentMode): void => {
        setSelectedAppointmentTypeId(undefined);
        setIsChainMode(val === APPOINTMENT_MODE.CHAINS);
        setIsSeriesMode(val === APPOINTMENT_MODE.SERIES);
    };

    const setIsSeriesMode = (value: boolean): void => {
        setIsSeriesModeStore(value);
        if (isDatePopoverOpened) toggleDatePopover(false);
    };

    const [activeStep, setActiveStep] = useState<Step>(initialStep);
    const [isDatePopoverOpened, toggleDatePopover] = useToggle([false, true]);
    const [isShowingTimeslotCalendar, toggle] = useToggle([false, true]);
    const { selected_location: selectedLocation } = useConfigStore();

    const onValuesChange = (values: AppointmentBookingFormValues): void => {
        setFormData(values);
    };

    // Filter data by selected location
    const dataFilteredBySelectedLocation: EnrichedPractitionerData | undefined = useMemo(():
        | EnrichedPractitionerData
        | undefined => {
        if (!formOptionData) return undefined;

        if (!selectedPracticeId) return formOptionData;

        return filterEnrichedDataByPracticeId(formOptionData, selectedPracticeId);
    }, [formOptionData, selectedPracticeId]);

    // Get all practices from unfiltered data (for Betriebsstätte dropdown)
    const allPracticesOptions = useMemo(() => {
        if (!formOptionData) return [];
        const options = getFormOptions(formOptionData, selectedCalendarId, selectedLocation?.key);
        return options.all_practices;
    }, [formOptionData, selectedCalendarId, selectedLocation?.key]);

    // Get filtered practitioners/appointments (for other dropdowns)
    const formOptions: AppointmentFormOptions | undefined = useMemo((): AppointmentFormOptions | undefined => {
        if (dataFilteredBySelectedLocation) {
            return getFormOptions(dataFilteredBySelectedLocation, selectedCalendarId, selectedLocation?.key);
        }

        return undefined;
    }, [dataFilteredBySelectedLocation, selectedCalendarId, selectedLocation?.key]);

    const allAppointmentTypes = useMemo(() => {
        if (!formOptions) return [];

        const allAppointmentTypes = [...formOptions.appointment_types, ...formOptions.unmapped_schedule_types];

        // In case the appointment type is neither mapped nor unmapped, we need to add it to the list
        // so the user can select it and be forced to select a valid appointment type
        if (selectedAppointmentTypeId) {
            const unknownAppointmentType = allAppointmentTypes.find((apt) => apt.value === selectedAppointmentTypeId);
            if (!unknownAppointmentType) {
                allAppointmentTypes.push({
                    label: 'Unbekannt', // Unknown
                    value: selectedAppointmentTypeId,
                    default_duration: {
                        value: initialValues?.duration ?? 0,
                        unit: 'min',
                    },
                    is_mapped: false,
                    is_offered: false, // force the user to select a valid appointment type
                    appointment_type: AppointmentType.SINGLE, // unknown types default to single
                });
            }
        }

        return allAppointmentTypes;
    }, [formOptions, selectedAppointmentTypeId, initialValues]);

    // Filter appointment types based on isSeriesMode
    const filteredAppointmentTypes = useMemo(() => {
        return allAppointmentTypes.filter((apt) =>
            isSeriesMode
                ? apt.appointment_type === AppointmentType.SERIES
                : apt.appointment_type === AppointmentType.SINGLE
        );
    }, [allAppointmentTypes, isSeriesMode]);

    const calendarOptions = buildCalendarOptionsByMode(
        formOptions,
        dataFilteredBySelectedLocation,
        isSeriesMode,
        isSeriesAppointmentTypeLocked ? selectedCalendarId : undefined
    );

    const isSeriesAllowed = allAppointmentTypes.some(
        (apt) => apt.value === selectedAppointmentTypeId && apt.appointment_type === AppointmentType.SERIES
    );

    const aptTypeInsuranceMap: AppointmentTypeInsurances = new Map(
        formOptionData?.appointment_types.map((type) => {
            const insurances: Insurance[] = [];
            if (type.is_for_public_insurance) insurances.push(Insurance.PUBLIC);
            if (type.is_for_private_insurance) insurances.push(Insurance.PRIVATE);
            if (type.is_for_self_payers) insurances.push(Insurance.SELF_PAYER);
            return [type.id, insurances];
        })
    );

    const onSelectAppointmentTypeIdUpdateDefaultDuration = (
        id: string | undefined,
        form: UseFormReturnType<AppointmentBookingFormValues>
    ): void => {
        if (id && formOptions) {
            const durationInMinutes = getDefaultDurationFromAppointmentTypeId(id, formOptions);

            if (durationInMinutes) {
                form.setFieldValue('duration', durationInMinutes);
            }
        }
    };

    const handleAppointmentTypeSelection = (
        id: string | undefined,
        form: UseFormReturnType<AppointmentBookingFormValues>
    ): void => {
        form.getInputProps('apt_type_id_or_schedule_type_pvs_id').onChange(id);
        setSelectedAppointmentTypeId(id);

        if (!id) {
            toggleDatePopover(false);
            setActiveStep(Step.APPOINTMENT_TYPE);
            return;
        }

        const selectedType = allAppointmentTypes.find((apt) => apt.value === id);

        // Auto-set series mode based on appointment type
        const isSeries = selectedType?.appointment_type === AppointmentType.SERIES;
        if (isSeries !== isSeriesMode) {
            setIsSeriesMode(isSeries);
        }

        const isOffered = selectedType?.is_offered;
        form.getInputProps('is_offered').onChange(isOffered);

        // If the appointment type is the same as the initial value, we don't need to update the duration
        if (id === initialValues.apt_type_id_or_schedule_type_pvs_id) {
            form.setFieldValue('duration', initialValues.duration);
        } else {
            onSelectAppointmentTypeIdUpdateDefaultDuration(id, form);
        }

        if (isOffered) {
            if (isSeries) {
                setActiveStep(Step.ADDITIONAL_INFO);
            } else {
                setActiveStep(Step.TIMESLOT);
                toggleDatePopover();
            }
        } else {
            setActiveStep(Step.APPOINTMENT_TYPE);
        }
    };

    if (!formOptions || isFormOptionDataLoading) {
        return (
            <div className="flex items-center justify-center">
                <Loader />
            </div>
        );
    }

    // Compute data for AppointmentModeSelector
    const hasSeriesTypes = allAppointmentTypes.some((apt) => apt.appointment_type === AppointmentType.SERIES);

    const handleToggleTimeslotCalendar = (): void => {
        if (isShowingTimeslotCalendar) {
            abortAvailabilityInteraction(PICKER_INTERACTION_KEY);
            toggle();
            return;
        }

        startAvailabilityInteraction({ key: PICKER_INTERACTION_KEY, mode: 'single', action: 'open_slots' });

        // Nothing will render, so nothing would ever settle this interaction.
        if (formOptions.practitioners.length === 0) {
            settleAvailabilityInteraction({
                key: PICKER_INTERACTION_KEY,
                outcome: 'failure',
                reason: 'availability_no_calendar',
            });
        }

        toggle();
    };

    return (
        <OverlaySection className="shadow-sm">
            <motion.div
                initial={{ opacity: allowTransition ? 0 : 1 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, delay: 0.3 }}
                className="w-full"
            >
                <Form
                    handleSubmit={() => {}} // :(
                    initialValues={initialValues}
                    className="flex w-full flex-col gap-4"
                    schema={book_appointment_form_schema}
                    onValuesChange={onValuesChange}
                    validateInputOnChange
                    checkIfValid={setIsFormValid}
                >
                    {(form) => (
                        <>
                            <FormSection>
                                {options.enable_location && (
                                    <FocusTrap active={enableSteps && activeStep === Step.PRACTICE}>
                                        <Select
                                            comboboxProps={{ withinPortal: false }}
                                            classNames={{
                                                dropdown: 'shadow-sm',
                                                label: 'font-bold',
                                            }}
                                            searchable
                                            allowDeselect={false}
                                            label="Betriebsstätte" // TODO: i18n
                                            placeholder="Behandler:in auswählen"
                                            data={allPracticesOptions}
                                            value={selectedPracticeId}
                                            onOptionSubmit={(value) => {
                                                form.getInputProps('practice_doc_cirrus_id').onChange(value);
                                                if (isChainMode && value !== selectedPracticeId) {
                                                    resetChainSelection();
                                                }
                                                setSelectedPracticeId(value);
                                                if (activeStep === Step.PRACTICE) {
                                                    setActiveStep(Step.INSURANCE);
                                                }
                                            }}
                                            disabled={isShowingTimeslotCalendar || isBookingLoading}
                                        />
                                    </FocusTrap>
                                )}
                                <FocusTrap active={enableSteps && activeStep === Step.INSURANCE}>
                                    <InputWrapper
                                        label="Versicherungsart"
                                        error={
                                            checkInsuranceIsOffered(
                                                form.values.apt_type_id_or_schedule_type_pvs_id,
                                                form.values.insurance,
                                                aptTypeInsuranceMap
                                            )
                                                ? ''
                                                : 'Der ausgewählte Terminart ist für diese Versicherungsart nicht verfügbar.'
                                        }
                                        classNames={{
                                            error: 'm-1 text-orange-600',
                                            label: 'font-bold',
                                        }}
                                    >
                                        <Select
                                            comboboxProps={{ withinPortal: false }}
                                            classNames={{
                                                dropdown: 'shadow-sm',
                                            }}
                                            searchable
                                            allowDeselect={false}
                                            data={[...new Set(Object.values(Insurance))]}
                                            {...form.getInputProps('insurance')}
                                            onOptionSubmit={(value) => {
                                                if (isChainMode && value !== String(form.values.insurance)) {
                                                    resetChainSelection();
                                                }
                                                form.getInputProps('insurance').onChange(value);
                                                if (activeStep === Step.INSURANCE) {
                                                    setActiveStep(Step.PRACTITIONER);
                                                }
                                            }}
                                            disabled={isShowingTimeslotCalendar || isBookingLoading}
                                        />
                                    </InputWrapper>
                                </FocusTrap>
                            </FormSection>
                            {!(enableSteps && [Step.PRACTICE, Step.INSURANCE].includes(activeStep)) && (
                                <AppointmentModeSelector
                                    selectedMode={selectedMode}
                                    onChange={(val) => {
                                        handleAppointmentTypeSelection('', form);
                                        setSelectedMode(val);
                                        if (val === APPOINTMENT_MODE.CHAINS) return;
                                        // autoselect if only one calendar
                                        const newOptions = buildCalendarOptionsByMode(
                                            formOptions,
                                            dataFilteredBySelectedLocation,
                                            val === APPOINTMENT_MODE.SERIES
                                        );
                                        const singleId = getSingleCalendarId(newOptions);
                                        if (singleId) {
                                            form.getInputProps('calendar_pvs_id').onChange(singleId);
                                            setSelectedCalendarId(singleId);
                                        }
                                    }}
                                    disabled={isSeriesAppointmentTypeLocked || !!editingAppointment || isBookingLoading}
                                    data={getAppointmentModeSelectorData(
                                        hasSeriesTypes,
                                        !isChainsLoading &&
                                            !chainsError &&
                                            filterChainConfigs(chainConfigs, {
                                                practiceId: selectedPracticeId,
                                                instanceId: formOptionData?.instance_id,
                                                insuranceType: insuranceToInsuranceType(form.values.insurance),
                                                isReturning: isReturningPatient,
                                            }).length > 0,
                                        appointmentsFormDraftCount > 1
                                    )}
                                >
                                    <PatientTypeSwitch
                                        practiceId={selectedPracticeId}
                                        instanceId={formOptionData?.instance_id}
                                        insuranceType={insuranceToInsuranceType(form.values.insurance)}
                                        chainConfigs={chainConfigs}
                                        disabled={isBookingLoading}
                                    />
                                </AppointmentModeSelector>
                            )}
                            {isChainMode && chainBookingStep === ChainBookingStep.CONFIGURE && formOptionData && (
                                <ChainBookingConfigurator
                                    key={selectedPracticeId ?? 'no-practice'}
                                    practiceId={selectedPracticeId}
                                    instanceId={formOptionData.instance_id}
                                    insuranceType={insuranceToInsuranceType(form.values.insurance)}
                                    chainConfigs={chainConfigs}
                                    isChainsLoading={isChainsLoading}
                                    chainsError={chainsError}
                                    refetchChains={refetchChains}
                                />
                            )}
                            {!isChainMode && (
                                <>
                                    {/* Practitioner calendars and appointment type */}
                                    <FormSection
                                        classNames={{
                                            wrapper: cn(
                                                'transition duration-500',
                                                enableSteps &&
                                                    [Step.PRACTICE, Step.INSURANCE].includes(activeStep) &&
                                                    'pointer-events-none opacity-0'
                                            ),
                                        }}
                                    >
                                        <CustomCalendarSelect
                                            autoFocusActive={enableSteps && activeStep === Step.PRACTITIONER}
                                            label="Kalender" // TODO: i18n
                                            defaultCalendarDocCirrusId={selectedCalendarId}
                                            data={calendarOptions}
                                            setSelectedCalendarId={(calendar_id) => {
                                                form.getInputProps('calendar_pvs_id').onChange(calendar_id);
                                                setSelectedCalendarId(calendar_id);
                                                if (activeStep === Step.PRACTITIONER && calendar_id) {
                                                    setActiveStep(Step.APPOINTMENT_TYPE);
                                                }
                                            }}
                                            disabled={isShowingTimeslotCalendar || isBookingLoading}
                                        />
                                        <CustomAppointmentTypeSelect
                                            autoFocusActive={enableSteps && activeStep === Step.APPOINTMENT_TYPE}
                                            label="Terminart" // TODO: i18n
                                            placeholder="Terminart auswählen"
                                            defaultAptTypeIdOrScheduleTypeId={selectedAppointmentTypeId}
                                            setSelectedAppointmentTypeId={(id) =>
                                                handleAppointmentTypeSelection(id, form)
                                            }
                                            data={
                                                isSeriesAppointmentTypeLocked
                                                    ? allAppointmentTypes
                                                    : filteredAppointmentTypes
                                            }
                                            disabled={
                                                isShowingTimeslotCalendar ||
                                                isSeriesAppointmentTypeLocked ||
                                                isBookingLoading
                                            }
                                        />
                                    </FormSection>
                                    {!isSeriesMode && (
                                        <>
                                            <Popover
                                                classNames={{
                                                    dropdown: 'shadow-lg',
                                                }}
                                                position="bottom-end"
                                                opened={isDatePopoverOpened}
                                                onChange={toggleDatePopover}
                                                disabled={isShowingTimeslotCalendar}
                                                onClose={() => {
                                                    if (activeStep === Step.TIMESLOT) {
                                                        setActiveStep(Step.ADDITIONAL_INFO);
                                                    }
                                                }}
                                                trapFocus
                                            >
                                                <div
                                                    className={cn(
                                                        'transition duration-500',
                                                        enableSteps &&
                                                            [
                                                                Step.PRACTICE,
                                                                Step.INSURANCE,
                                                                Step.PRACTITIONER,
                                                                Step.APPOINTMENT_TYPE,
                                                            ].includes(activeStep) &&
                                                            'opacity-0'
                                                    )}
                                                >
                                                    <label htmlFor="date-duration" className="text-sm font-bold">
                                                        Termin am
                                                    </label>
                                                    <div
                                                        className={cn(
                                                            'flex w-full justify-between gap-2 rounded border border-slate-300 bg-slate-100 p-2 transition duration-500',
                                                            isShowingTimeslotCalendar &&
                                                                'border-transparent text-slate-400'
                                                        )}
                                                    >
                                                        <Paragraph className="text-sm">
                                                            <span
                                                                className={cn(
                                                                    `relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-full after:bg-black after:content-['']`,
                                                                    !form.getValues().date_time &&
                                                                        'invisible after:visible',
                                                                    isShowingTimeslotCalendar && 'after:bg-slate-400'
                                                                )}
                                                            >
                                                                {dayjs(form.getValues().date_time).format(
                                                                    'ddd DD MMM YYYY'
                                                                )}
                                                            </span>
                                                            {` um ${
                                                                form.getValues().date_time
                                                                    ? dayjs(form.getValues().date_time).format('HH:mm')
                                                                    : '--:--'
                                                            } für ${form.getValues().duration} min`}
                                                        </Paragraph>
                                                        <Popover.Target>
                                                            <ActionIcon
                                                                variant="transparent"
                                                                aria-label="Date and Duration"
                                                                onClick={() => toggleDatePopover()}
                                                                disabled={isShowingTimeslotCalendar}
                                                                className="disabled:bg-transparent"
                                                                size="sm"
                                                            >
                                                                <Icon
                                                                    icon={Pencil}
                                                                    color={
                                                                        isShowingTimeslotCalendar
                                                                            ? 'text-slate-400'
                                                                            : 'text-black'
                                                                    }
                                                                />
                                                            </ActionIcon>
                                                        </Popover.Target>
                                                    </div>
                                                </div>
                                                <Popover.Dropdown className="flex flex-col gap-3">
                                                    <DateTimePickerInput
                                                        value={String(form.getInputProps('date_time').defaultValue)}
                                                        onChange={(value) => {
                                                            setDefaultStartDate(value);
                                                            form.getInputProps('date_time').onChange(value);
                                                        }}
                                                        onClose={() => toggleDatePopover()}
                                                        additionalComponents={
                                                            <DurationInput
                                                                initialValue={initialValues.duration}
                                                                form={form}
                                                            />
                                                        }
                                                    />
                                                </Popover.Dropdown>
                                            </Popover>
                                            <Switch
                                                label="Alle Kalender anzeigen"
                                                onChange={handleToggleTimeslotCalendar}
                                                checked={isShowingTimeslotCalendar}
                                                disabled={
                                                    !checkInsuranceIsOffered(
                                                        form.values.apt_type_id_or_schedule_type_pvs_id,
                                                        form.values.insurance,
                                                        aptTypeInsuranceMap
                                                    )
                                                }
                                                className={cn(
                                                    'w-52 transition duration-500',
                                                    enableSteps &&
                                                        [
                                                            Step.PRACTICE,
                                                            Step.INSURANCE,
                                                            Step.PRACTITIONER,
                                                            Step.APPOINTMENT_TYPE,
                                                        ].includes(activeStep) &&
                                                        'pointer-events-none opacity-0'
                                                )}
                                            />
                                            <AnimatePresence>
                                                {isShowingTimeslotCalendar && formOptions.practitioners.length > 0 && (
                                                    <TimeslotCalendarAccordion
                                                        key="timeslot-calendar-accordion"
                                                        selectedAppointment={dataFilteredBySelectedLocation?.appointment_types.find(
                                                            (appointment) =>
                                                                appointment.id === selectedAppointmentTypeId
                                                        )}
                                                        practitioners={
                                                            dataFilteredBySelectedLocation?.practitioners ?? []
                                                        }
                                                        onSlotSelect={(calendar_id, slot) => {
                                                            form.setFieldValue('calendar_pvs_id', calendar_id);
                                                            form.setFieldValue('date_time', slot);
                                                        }}
                                                        selectedCalendar={form.getValues().calendar_pvs_id}
                                                        selectedSlot={form.getValues().date_time}
                                                        insurance={form.getValues().insurance}
                                                        defaultStartDate={defaultStartDate}
                                                    />
                                                )}
                                            </AnimatePresence>
                                        </>
                                    )}
                                    <div
                                        className={cn(
                                            'flex flex-col gap-2 transition duration-500',
                                            enableSteps && activeStep !== Step.ADDITIONAL_INFO && 'opacity-0'
                                        )}
                                    >
                                        {options.enable_priority && (
                                            <ImportanceSelector
                                                value={form.getValues().priority}
                                                onChange={(value) => {
                                                    form.getInputProps('priority').onChange(value);
                                                }}
                                            />
                                        )}
                                        <TextInput
                                            label="Bezeichnung (wahlweise)"
                                            classNames={{ label: 'font-bold' }}
                                            placeholder="Bezeichnung"
                                            {...form.getInputProps('description')}
                                        />
                                        <Textarea
                                            label="Termin Info (wahlweise)"
                                            classNames={{ label: 'font-bold' }}
                                            placeholder="Informationen"
                                            {...form.getInputProps('info')}
                                        />

                                        <AnimatePresence>
                                            {isSeriesMode && isSeriesAllowed && (
                                                <motion.div
                                                    key="series-configurator"
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                                                    style={{ overflow: 'hidden' }}
                                                >
                                                    <CollectionConfigurator />
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </>
                            )}

                            {!disableCancel && (
                                <div className="flex w-full">
                                    <Button
                                        variant="transparent"
                                        leftSection={<Icon icon={Trash2} />}
                                        classNames={{ root: '!p-0' }}
                                        onClick={onDelete}
                                        className="text-red-600 hover:text-red-500"
                                    >
                                        Löschen
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </Form>
            </motion.div>
        </OverlaySection>
    );
};
